import { NotificationCategory, NotificationSeverity, type NotificationDto } from 'api';

/**
 * Presentación visual derivada de una notificación: icono PrimeNG y colores por severidad.
 *
 * El icono se elige por `type` (catálogo del backend) cuando se conoce, con fallback por
 * `category` y finalmente un icono genérico —la lista de `type` no es cerrada, así que nunca
 * asumimos que un tipo nuevo está mapeado—. El color se deriva siempre de `severity`.
 */
export interface NotificationVisual {
  /** Clase de icono PrimeNG (ej. `pi pi-box`). */
  icon: string;
  /** Severidad mapeada para PrimeNG (`p-tag`, toasts, etc.). */
  severity: 'info' | 'warn' | 'danger';
  /** Clase Tailwind de color de texto para el icono. */
  colorClass: string;
}

const SEVERITY_MAP: Record<NotificationSeverity, { severity: NotificationVisual['severity']; colorClass: string }> = {
  [NotificationSeverity.Info]: { severity: 'info', colorClass: 'text-blue-500' },
  [NotificationSeverity.Warning]: { severity: 'warn', colorClass: 'text-amber-500' },
  [NotificationSeverity.Critical]: { severity: 'danger', colorClass: 'text-red-500' },
};

/** Icono por `type` exacto (catálogo §4 del contrato). */
const TYPE_ICON_MAP: Readonly<Record<string, string>> = {
  'inventory.low-stock': 'pi pi-exclamation-triangle',
  'inventory.movement-voided': 'pi pi-ban',
  'inventory.movement-stale': 'pi pi-clock',
  'inventory.cost-updated': 'pi pi-sync',
  'logistics.late-delivery': 'pi pi-truck',
  'logistics.delivery-note-cancelled': 'pi pi-ban',
  'sales.invoice-created': 'pi pi-file',
  'sales.credit-note-issued': 'pi pi-replay',
  'sales.seller-document-created': 'pi pi-file-edit',
  'sales.commissions-unpaid': 'pi pi-dollar',
  'pricing.price-updated': 'pi pi-tag',
  'ecommerce.order-created': 'pi pi-shopping-cart',
  'ecommerce.account-requested': 'pi pi-user-plus',
  'finance.bank-balance-adjusted': 'pi pi-wallet',
  'finance.transaction-voided': 'pi pi-ban',
  'finance.payment-request-pending': 'pi pi-credit-card',
  'finance.official-rate-stale': 'pi pi-history',
  'finance.expenses-overdue': 'pi pi-calendar-times',
  'finance.receivable-overdue': 'pi pi-arrow-down',
  'finance.payable-overdue': 'pi pi-arrow-up',
  'credit.limit-exceeded': 'pi pi-exclamation-circle',
  'purchasing.material-request-pending': 'pi pi-list',
  'purchasing.receipt-posted': 'pi pi-check-square',
  'purchasing.cost-updated': 'pi pi-sync',
  'hr.contract-expiring': 'pi pi-id-card',
  'hr.vacation-request-pending': 'pi pi-calendar',
  'support.ticket-created': 'pi pi-plus-circle',
  'support.ticket-unassigned': 'pi pi-inbox',
  'support.ticket-assigned': 'pi pi-ticket',
  'support.agent-replied': 'pi pi-reply',
  'support.customer-replied': 'pi pi-comment',
  'support.status-changed': 'pi pi-sync',
  'support.sla-breached': 'pi pi-stopwatch',
  'pos.cash-discrepancy': 'pi pi-wallet',
  'pos.print-failed': 'pi pi-print',
  'projects.task-assigned': 'pi pi-user-plus',
  'projects.task-mentioned': 'pi pi-at',
  'projects.task-state-changed': 'pi pi-sync',
  'projects.task-commented': 'pi pi-comment',
  'projects.task-start': 'pi pi-play',
  'projects.task-due': 'pi pi-calendar-times',
  'projects.task-stale': 'pi pi-clock',
  'projects.task-reminder': 'pi pi-bell',
  'projects.milestone-due': 'pi pi-flag',
};

/** Icono de fallback por categoría cuando el `type` no está mapeado. */
const CATEGORY_ICON_MAP: Record<NotificationCategory, string> = {
  [NotificationCategory.Inventory]: 'pi pi-box',
  [NotificationCategory.Logistics]: 'pi pi-truck',
  [NotificationCategory.Hr]: 'pi pi-users',
  [NotificationCategory.Support]: 'pi pi-headphones',
  [NotificationCategory.Finance]: 'pi pi-dollar',
  [NotificationCategory.Sales]: 'pi pi-shopping-cart',
  [NotificationCategory.Purchasing]: 'pi pi-shopping-bag',
  [NotificationCategory.Pos]: 'pi pi-desktop',
  [NotificationCategory.System]: 'pi pi-cog',
  [NotificationCategory.Projects]: 'pi pi-objects-column',
};

const GENERIC_ICON = 'pi pi-bell';

/** Severidades por índice, para tolerar que SignalR serialice el enum como número (0/1/2). */
const SEVERITY_BY_INDEX = [
  NotificationSeverity.Info,
  NotificationSeverity.Warning,
  NotificationSeverity.Critical,
] as const;

/**
 * Normaliza la severidad a un valor del enum, tolerando que el backend la envíe como string
 * (REST) o como número (SignalR puede serializar enums como índice). Fallback: `Info`.
 */
export function normalizeSeverity(value: unknown): NotificationSeverity {
  if (typeof value === 'number') {
    return SEVERITY_BY_INDEX[value] ?? NotificationSeverity.Info;
  }
  if (typeof value === 'string') {
    const match = SEVERITY_BY_INDEX.find((s) => s.toLowerCase() === value.toLowerCase());
    if (match) return match;
  }
  return NotificationSeverity.Info;
}

/** Resuelve el icono de una notificación: por `type`, luego `category`, luego genérico. */
export function iconFor(notification: NotificationDto): string {
  if (notification.type && TYPE_ICON_MAP[notification.type]) {
    return TYPE_ICON_MAP[notification.type];
  }
  const category = notification.category;
  if (category && CATEGORY_ICON_MAP[category]) {
    return CATEGORY_ICON_MAP[category];
  }
  return GENERIC_ICON;
}

/** Mapea la severidad del backend a la severidad de PrimeNG (con fallback `info`). */
export function severityToPrimeNg(severity: unknown): NotificationVisual['severity'] {
  return SEVERITY_MAP[normalizeSeverity(severity)].severity;
}

/** Construye la presentación visual completa de una notificación. */
export function visualFor(notification: NotificationDto): NotificationVisual {
  const sev = SEVERITY_MAP[normalizeSeverity(notification.severity)];
  return {
    icon: iconFor(notification),
    severity: sev.severity,
    colorClass: sev.colorClass,
  };
}
